import * as Sentry from '@sentry/vue';
import { Action, getModule, Module, Mutation, VuexModule } from 'vuex-module-decorators';
import { Address, TaxInfo } from 'types/form';
import {
	createUser,
	getAddress,
	getPhoneData,
	getUser,
	setUserFirstAndLastNames,
	syncNewUserWithSession,
	updateInvestmentEntity,
	updateUserInSession
} from '@api/user';
import { getKnownLoginDevices, updateEmailAddress } from '@api/settings';
import { PhoneData, PhoneNumber, User, UserSignupForm } from 'types/user';
import { submitIndividualAccountContactDetails, submitIndividualAccountTaxReportingDetails } from '@api/checkout';
import { app } from '@store/modules/app';
import { getInvitationDetails } from '@api/referral';
import { getTwoFactorStatus } from '@api/login';
import { IndividualAccountContactDetails } from 'types/checkout';
import { investmentEntity } from '@store/modules/investment-entity';
import { InvestmentEntity } from 'types/investment-entity';
import { InvitationDetailsResponse } from 'types/referral';
import { KnownDevice } from 'types/settings';
import { orderForm } from '@store/modules/order-form';
import { RawLocation } from 'vue-router';
import router from '@src/router';
import store from '..';
import { trackMixpanel } from '@utils/analytics';
import { TwoFactorType } from 'types/authentication';

export interface UserState {
	user: User;
	phoneData: PhoneData;
}
@Module({
	dynamic: true,
	namespaced: true,
	name: 'user',
	store
})
class UserModule extends VuexModule {
	user: User | null = null;

	address: Address = {
		address1: '',
		address2: '',
		city: '',
		state: '',
		zip: '',
		countryCode: ''
	};

	devices: Array<KnownDevice> = [];

	invitationDetails: InvitationDetailsResponse | null = null;

	phoneData: PhoneData = {
		twoFactorPhoneNumber: '',
		currentPhoneNumber: '',
		currentPhoneNumberValidated: false,
		allPhoneNumbers: []
	};
	twoFactorType: TwoFactorType | null = null;

	get isExistingInvestor(): boolean {
		return !!this.user?.entityDetails?.firstInvestmentDate;
	}

	get hasAddress(): boolean {
		return !!this.address.address1;
	}

	get hasIraEntity(): boolean {
		return this.user?.entityDetails.investmentEntities.some((entity) => entity.entityType === 'IRA') ?? false;
	}

	get hasFirstInvestment(): boolean {
		return !!this.user?.entityDetails?.firstInvestmentDate;
	}

	get hasFirstLastName(): boolean {
		return !!(this.user && this.user.firstName && this.user.lastName);
	}

	get hasDateOfBirth(): boolean {
		return !!this.user?.hasSubmittedDob;
	}

	get hasEmailAddress(): boolean {
		return !!this.user?.email;
	}

	get getDeviceById(): (deviceId: string) => KnownDevice | undefined {
		return (deviceId: string): KnownDevice | undefined => {
			return this.devices.find((device) => device.deviceId === deviceId);
		};
	}

	get ssnNeedsUpdating(): boolean {
		return this.user?.ssnNeedsUpdating || !this.user?.hasSubmittedSsn;
	}

	get iraEntities(): Array<InvestmentEntity> {
		return this.user?.entityDetails.investmentEntities.filter((entity) => entity.entityType === 'IRA') ?? [];
	}

	@Action
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	async createUser(userSignupForm: UserSignupForm): Promise<any> {
		try {
			const response = await createUser(userSignupForm);
			await syncNewUserWithSession(response);
			app.SET_OAUTH_TOKEN(response.accessToken);
			app.SET_IS_AUTHENTICATED(true);
			await this.getUser();
			return undefined;
		} catch (error) {
			return error;
		}
	}

	@Action
	async getUser(): Promise<void> {
		const user = await getUser();
		this.UPDATE_USER(user);

		const selectedInvestmentEntity = user.entityDetails.investmentEntities.filter(
			(investmentEntity) => investmentEntity.selected
		)[0];

		const entity = selectedInvestmentEntity;

		await investmentEntity.setInvestmentEntity(entity);
		Sentry.setContext('user', {
			id: user.id
		});

		await this.setInvitationDetails();
	}

	@Action
	private async userHasInvestmentEntity(investmentEntityId: string): Promise<boolean> {
		let hasInvestmentEntity =
			this.user?.entityDetails?.investmentEntities?.some(
				(entity) => entity.investmentEntityId === investmentEntityId
			) ?? false;

		if (!hasInvestmentEntity) {
			await this.getUser();

			hasInvestmentEntity =
				this.user?.entityDetails?.investmentEntities?.some(
					(entity) => entity.investmentEntityId === investmentEntityId
				) ?? false;
		}

		return hasInvestmentEntity;
	}

	@Action
	private async updateInvestmentEntityAndTrackEvents(investmentEntityId: string): Promise<{
		previousEntity: InvestmentEntity;
		newEntity: InvestmentEntity;
	}> {
		const entityIsValid = await this.userHasInvestmentEntity(investmentEntityId);
		const previousEntity = this.user?.entityDetails.investmentEntities.find((entity) => entity.selected);
		const newEntity = this.user?.entityDetails.investmentEntities.find(
			(entity) => entity.investmentEntityId === investmentEntityId
		);

		if (entityIsValid && previousEntity && newEntity) {
			await updateInvestmentEntity(investmentEntityId);

			trackMixpanel('Selected Investment Entity Switched', {
				switchedFrom: previousEntity?.investmentEntityId,
				switchedTo: investmentEntityId,
				fromInvestmentEntityType: previousEntity?.entityType,
				toInvestmentEntityType: newEntity?.entityType
			});
		} else {
			Sentry.captureMessage('Cannot switch to entity not associated with user.');
			return Promise.reject();
		}

		return Promise.resolve({ previousEntity, newEntity });
	}

	@Action({ rawError: true })
	public async updateSelectedEntity(investmentEntityId: string): Promise<void> {
		await this.updateInvestmentEntityAndTrackEvents(investmentEntityId);
		window.location.reload();
	}

	@Action({ rawError: true })
	public async updateSelectedEntityWithoutReload({
		investmentEntityId,
		nextRoute
	}: {
		investmentEntityId: string;
		nextRoute: { route: RawLocation; next?: (to?: RawLocation) => void };
	}): Promise<void> {
		const { previousEntity, newEntity } = await this.updateInvestmentEntityAndTrackEvents(investmentEntityId);
		window.fundriseApp.investmentEntityId = investmentEntityId;
		previousEntity.selected = false;
		newEntity.selected = true;

		await investmentEntity.setInvestmentEntity(newEntity);
		try {
			if (!nextRoute.next) {
				router.push(nextRoute.route);
			} else {
				nextRoute.next(nextRoute.route);
			}
		} catch {}
	}

	@Action
	async refreshUser(): Promise<void> {
		await this.getUser();
		await investmentEntity.refreshPositionsAndPerformance();
	}

	@Action
	public async storeAddress(force = false): Promise<void> {
		if (!this.hasAddress || force) {
			const addressData = await getAddress();
			if (addressData) {
				this.UPDATE_ADDRESS(addressData);
			}
		}
	}

	@Action
	public async setInvitationDetails(): Promise<void> {
		const invitationDetailsData = await getInvitationDetails();
		this.UPDATE_INVITATION_DETAILS(invitationDetailsData);
	}

	@Action
	public async storePhoneData(force = false): Promise<void> {
		if (!this.phoneData.currentPhoneNumber || force) {
			const phoneData = await getPhoneData();
			this.UPDATE_PHONE_DATA(phoneData);
		}
	}

	@Action
	public async setFirstAndLastNames(payload: { firstName: string; lastName: string }): Promise<void> {
		await setUserFirstAndLastNames(payload.firstName, payload.lastName);
		await updateUserInSession();
		await this.getUser();
	}

	@Action
	public async storeDevices(): Promise<void> {
		if (this.devices.length === 0) {
			const devices = await getKnownLoginDevices();
			this.UPDATE_DEVICES(devices);
		}
	}

	@Action
	public clearDevices(): void {
		if (this.devices.length > 0) {
			this.UPDATE_DEVICES([]);
		}
	}

	@Action({ rawError: true })
	public async setUserContactDetails(payload: IndividualAccountContactDetails): Promise<string> {
		await submitIndividualAccountContactDetails(payload);
		await updateUserInSession();
		await updateInvestmentEntity(investmentEntity.investmentEntityId);
		return 'checkout-tax-reporting-details';
	}

	@Action({ rawError: true })
	public async setUserTaxDetails(payload: TaxInfo): Promise<string> {
		await submitIndividualAccountTaxReportingDetails(payload);
		orderForm.UPDATE_HAS_SUBMITTED_SSN(true);
		orderForm.UPDATE_HAS_SUBMITTED_DOB(true);
		orderForm.UPDATE_HAS_SUBMITTED_ACCOUNT_DETAILS(true);

		await updateUserInSession();
		await updateInvestmentEntity(investmentEntity.investmentEntityId);
		return 'checkout-payment-amount';
	}

	@Action({ rawError: true })
	public async setUserTaxDetailsFromProfile(payload: TaxInfo): Promise<void> {
		await submitIndividualAccountTaxReportingDetails(payload);
		this.refreshUser();
	}

	@Action({ rawError: true })
	public async storeTwoFactorType(force?: boolean): Promise<void> {
		if (!this.twoFactorType || force) {
			const twoFactorType = await getTwoFactorStatus();
			this.UPDATE_TWO_FACTOR_TYPE(twoFactorType);
		}
	}

	@Action({ rawError: true })
	public async updateEmailAddress(updatedEmail: string): Promise<void> {
		await updateEmailAddress(updatedEmail);
		await this.getUser();
	}

	@Mutation
	public UPDATE_USER(user: User): void {
		this.user = user;
	}

	@Mutation
	public UPDATE_INVITATION_DETAILS(invitationDetails: InvitationDetailsResponse) {
		this.invitationDetails = invitationDetails;
	}

	@Mutation
	public UPDATE_PHONE_DATA(data: PhoneData): void {
		this.phoneData = data;
	}

	@Mutation
	public ADD_PHONE_NUMBER(newPhone: PhoneNumber): void {
		if (!this.phoneData.allPhoneNumbers.some((existingPhone) => existingPhone.id === newPhone.id)) {
			this.phoneData.allPhoneNumbers.push(newPhone);
		}
	}

	@Mutation
	public UPDATE_ADDRESS(address: Address): void {
		this.address = address;
	}

	@Mutation
	public UPDATE_DEVICES(devices: Array<KnownDevice>): void {
		this.devices = devices;
	}

	@Mutation
	public UPDATE_TWO_FACTOR_TYPE(twoFactorType: TwoFactorType): void {
		this.twoFactorType = twoFactorType;
	}
}

export const user = getModule(UserModule);
